import React, { useEffect, useCallback, useState, useMemo } from 'react';
import axios from 'axios';
import { Alert, AlertRecipient, Paginator as ModelPaginator } from '../model';
import { CheckboxWithImage, CheckboxWithStatus, Loading, MenuInRow, Modal, MultiSelectForStatus, MultiSelectState, Paginator, SearchResource, SortResource } from '../ui';
import { Link, useHistory } from 'react-router-dom';
import { apiBaseUrl, DefaultPageSize, MonitorStatusType, MonitorTransientStatusType, StatusColors } from "../Consts";
import { Empty } from '../empty';
import { IconCall, IconEmail, IconSlack, IconDiscord, IconSms, IconWebhook, RemoveButton } from '../images';
import { useQuery } from '../hooks/UseQuery';
import { Trans, useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { teamState, isOwnerOrMemberState } from '../atoms';
import { useAdjustPage, useHandleRequestError } from '../hooks';
import { AlertSelectors } from './AlertsSelectors';
import { Validations } from '../validator';
import { isFieldStringEmpty } from '../Validations';
import { AlertEdit } from '../alert-edit';
import { AlertSelector } from './AlertSelector';

const v = new Validations(new Alert());
v.addField("name", (v: any) => isFieldStringEmpty(v));

export const Alerts: React.FC = () => {

  const { t } = useTranslation();

  const { handleRequestError } = useHandleRequestError();
  const history = useHistory();
  const query = useQuery();
  const team = useRecoilValue(teamState);
  const isOwnerOrMember = useRecoilValue(isOwnerOrMemberState);
  const baseUrl = useMemo(() => team ? `/${team.slug}/alerts` : undefined, [team]);

  const [ selectors, setSelectors ] = useState<AlertSelectors>()
  const [ editingSelector, setEditingSelector ] = useState<Alert>()
  const [ removingAlert, setRemovingSelector ] = useState<Alert>()
  const [ paginator, setPaginator ] = useState<ModelPaginator>();

  const [ isSelectingForRemove, setIsSelectingForRemove ] = useState<boolean>(false);

  const queryText = useMemo(() => query.get("text") ?? undefined, [query]);
  const queryOrder = useMemo(() => query.get("order") ?? undefined, [query]);
  const queryDescending = useMemo(() => query.get("descending") ? query.get("descending") === "true" : false, [query]);
  const [updatingQuery, setUpdatingQuery] = useState(false);
  const { adjustPage, shouldFetchAndReset } = useAdjustPage(baseUrl);

  const fetch = useCallback(() => {
    if (team) {
      axios.get(`${apiBaseUrl}/${team.uuid}/alerts`, {params: {
        text: query.get("text"),
        order: query.get("order"),
        descending: query.get("descending"),
        recordOffset: query.get("page") ?
          (parseInt(query.get("page") as string) - 1) * DefaultPageSize : 0
      }})
        .then(response => {
          setSelectors(new AlertSelectors(response.data.alerts as Alert[]));
          setPaginator(response.data.paginator);
          adjustPage(response.data.paginator as ModelPaginator);
        })
        .catch(err => handleRequestError(err))
        .finally(() => setUpdatingQuery(false));
    }
  }, [team, query, adjustPage, handleRequestError]);

  const showRecipients = useCallback((as: AlertSelector) => {
    if (selectors && team) {
      if (!as.alert.recipients) {
        axios.get(`${apiBaseUrl}/${team.uuid}/alerts/${as.alert.uuid}/recipients`)
          .then(response => {
            as.alert.recipients = response.data.recipients as AlertRecipient[];
            setSelectors(selectors.updateFromRemote(as.alert).showRecipients(as));
          })
          .catch(err => handleRequestError(err))
          .finally(() => setUpdatingQuery(false));
      } else {
        setSelectors(selectors.showRecipients(as));
      }
    }
  }, [selectors, team, handleRequestError]);

  const hideRecipients = useCallback((as: AlertSelector) => {
    if (selectors) {
      setSelectors(selectors.hideRecipients(as));
    }
  }, [selectors]);

  const onUpdate = useCallback((alert: Alert) => {
    if (selectors) {
      setSelectors(selectors.updateFromRemote([alert]));
    }
    setEditingSelector(undefined);
  }, [selectors]);

  const execRemove = useCallback(() => {
    if (team && removingAlert) {
      setRemovingSelector(undefined)
      axios.delete(`${apiBaseUrl}/${team.uuid}/alerts/${removingAlert.uuid}`,
        {
          params: {
            text: query.get("text"),
            order: query.get("order"),
            descending: query.get("descending"),
            recordOffset: query.get("page") ?
              (parseInt(query.get("page") as string) - 1) * DefaultPageSize : 0
          }
        })
        .then(response => {
          setSelectors(new AlertSelectors(response.data.alerts as Alert[]));
          setPaginator(response.data.paginator);
          adjustPage(response.data.paginator as ModelPaginator);
        })
        .catch(err => handleRequestError(err));
        setRemovingSelector(undefined);
      }
  }, [team, removingAlert, query, adjustPage, handleRequestError]);

  const removeAlerts = useCallback(() => {
    if (team && selectors) {
      const uuids = selectors.list.filter(as => as.selected).map(as => as.alert.uuid);
      axios.delete(`${apiBaseUrl}/${team.uuid}/alerts`,
        {
          params: {
            text: query.get("text"),
            order: query.get("order"),
            descending: query.get("descending"),
            recordOffset: query.get("page") ?
              (parseInt(query.get("page") as string) - 1) * DefaultPageSize : 0,
            uuids: uuids
          }
        })
        .then(response => {
          setSelectors(new AlertSelectors(response.data.alerts as Alert[]));
          setPaginator(response.data.paginator);
          adjustPage(response.data.paginator as ModelPaginator);
        })
        .catch(err => handleRequestError(err));
      }
      setIsSelectingForRemove(false);
    }, [team, selectors, query, adjustPage, handleRequestError]);

  const changeQuery = useCallback((queryText?: string, queryOrder?: string, queryDescending?: boolean) => {
    if (baseUrl) {
      setUpdatingQuery(true);

      const _query = new URLSearchParams(query);
      if (queryText) {
        _query.set("text", queryText);
      } else {
        _query.delete("text");
        if (queryOrder) {
          _query.set("order", queryOrder);
        } else {
          _query.delete("order");
        }
        if (queryDescending) {
          _query.set("descending", queryDescending.toString());
        } else {
          _query.delete("descending");
        }
      }
      history.push(`${baseUrl}?${_query.toString()}`);
    }
  }, [baseUrl, history, query]);

  const onCancelEdit = useCallback(() => setEditingSelector(undefined), [])

  const renderStatuses = useCallback((as: AlertSelector) => {
    return [
      [as.alert.up, MonitorStatusType.Up, t("Up"), StatusColors.Up],
      [as.alert.degraded, MonitorStatusType.Degraded, t("Degraded"), StatusColors.Degraded],
      [as.alert.disrupted, MonitorStatusType.Disrupted, t("Disrupted"), StatusColors.Disrupted],
      [as.alert.down, MonitorStatusType.Down, t("Down"), StatusColors.Down],
      [as.alert.maintenance, MonitorStatusType.Maintenance, t("Maintenance"), StatusColors.Maintenance],
    ].map(([selected, status, title, color], i) =>
      selected?
        <div key={i} className="d-f a-i-c m-t-0.5 m-r-1.25 m-r-0-L" style={{ color: `${color}` }}>
          <CheckboxWithStatus
            unselectable={true}
            status={status as MonitorStatusType}
            transientStatus={MonitorTransientStatusType.Idle} />&nbsp;&nbsp;{title}
        </div>
      : null
    )
  }, [t]);

  useEffect(() => {
    if (shouldFetchAndReset()) {
      fetch();
    }
  }, [fetch, shouldFetchAndReset]);

  if (!selectors || !team || !paginator) {

    return <Loading />

  } else if (selectors.length === 0 && !queryText && !updatingQuery) {

    return (
      <Empty
        description={
          <span>
            <Trans t={t} i18nKey="No alerts <1/> added yet">
              No alerts<br/>added yet
            </Trans>
          </span>}
        actionTitle={t("Add an alert")}
        onAction={() => history.push(`/${team.slug}/alerts/new`)} />
    )

  } else {
    return (
      <>
        <div className="d-f fb-d-c m-h-a m-b-1 m-b-2-M p-h-0.5 p-h-2-M w-100% max-w-66-L t-c-nb">
          <div className="d-f p-l-0.5 p-v-1.5 m-t-0.5 fb-d-cr fb-d-r-L a-i-c-L">
            <div className="d-n d-f-L f-s-2 f-w-300 fb-n">
              {t("Alerts")}
            </div>
            <div className="fb-a w-100% p-h-2-L m-t-2 m-t-0-L">
              <SearchResource
                initValue={queryText}
                onChange={(value) => value !== queryText ? changeQuery(value) : null} />
            </div>
            <div className="d-f a-i-c fb-n">
              <div className="d-n-L f-s-1.5 f-w-300 fb-a">
                {t("Alerts")}
              </div>
              <div className="fb-n">
                <SortResource
                  options={{
                    "name": t("Name"),
                    "lastCreated": t("Last created"),
                    "lastModified": t("Last modified"),
                  }}
                  desc={queryDescending}
                  order={queryOrder ?? "name"}
                  disabled={queryText !== undefined}
                  onChange={(order: string, desc: boolean) => { changeQuery(undefined, order, desc) }} />
              </div>
            </div>
          </div>
          {selectors.length === 0 ?
            <div className="d-f h-5 a-i-c f-s-1 p-l-0.5">
              {t("No result. Try your search again with more specific keywords.")}
            </div>
          :
            <>
              <div className="d-f h-5 a-i-c b-sb-s b-c-lightestGray b-wb-0.1 f-w-b">
                <div className="d-f p-l-0.5 fb-a">
                  {isOwnerOrMember ?
                    <div className="d-f fb-n w-3">
                      <MultiSelectForStatus
                        onChange={() => setSelectors(selectors.changeMultiSelectState())}
                        state={selectors.multiSelectState} />
                    </div>
                  : null}
                  <div className="fb-a d-f a-i-c t-c-b">
                    {selectors.multiSelectState === MultiSelectState.None ?
                        t("Name")
                    :
                      <div onClick={() => setIsSelectingForRemove(true)} title={t("Delete")} className="d-f c-p t-c-nb">
                        <RemoveButton />
                      </div>
                    }
                  </div>
                </div>
                <div className="d-n d-f-L w-14 t-c-b">{t("Monitors")}</div>
                <div className="d-n d-f-L w-12 t-c-b">{t("Status")}</div>
                {isOwnerOrMember ?
                  <div className="d-f w-2"></div>
                : null}
              </div>

              { selectors?.list.map((as, i) =>
                <div key={i} className="d-f a-i-s p-v-0.75 b-sb-s b-c-lightestGray b-wb-0.1">
                  {isOwnerOrMember ?
                    <div className="d-f fb-n w-3 a-i-s m-l-0.5">
                      <CheckboxWithImage
                        size= {"large"}
                        disabled={!isOwnerOrMember}
                        type={"alert"}
                        round={true}
                        checked={as.selected}
                        onChange={isOwnerOrMember ? () => setSelectors(selectors.toggle(as)) : undefined}
                      />
                    </div>
                  : null}
                  <div className="d-f fb-a fb-d-c">
                    <div className="d-f fb-d-c fb-d-r-L">
                      <div className="d-f fb-a a-i-c h-2">
                        <div className="a-i-c t-o-h of-h ws-n t-o-e p-r-1">
                          {as.alert.name}
                        </div>
                      </div>
                      <div className="d-f d-n-L m-t-1 m-b-0.5 fb-w-w p-b-1">
                        {renderStatuses(as)}
                      </div>
                      <div className="d-f w-100% h-2 a-i-c w-14-L fb-n b-st-s b-w-1px b-c-lightestGray b-st-n-L p-t-1 p-t-0-L">
                        {as.alert.monitors.length > 0 ?
                          <div className="d-f fb-d-r">
                            <div>
                              {as.alert.monitors.length}&nbsp;
                              {as.alert.monitors.length > 1 ?
                                t("Monitors added")
                              :
                                t("Monitor added")
                              }
                            </div>
                            <div className="d-f d-n-L p-l-1">
                              <Link to={`/${team.slug}/monitors?alertUuid=${as.alert.uuid}`}>
                                {as.alert.monitors.length > 1 ?
                                  t("View Monitors")
                                :
                                  t("View Monitor")
                                }
                              </Link>
                            </div>

                          </div>
                        :
                          <>
                            <div className="t-c-r">No monitors added</div>
                            <div className="d-f d-n-L p-l-1">
                              <Link to={`/${team.slug}/monitors`}>
                                {t("Add  Monitors")}
                              </Link>
                            </div>
                          </>
                        }
                      </div>
                    </div>
                    <div className="d-f fb-d-cr fb-d-r-L">
                      <div className="d-f fb-a fb-d-c m-t-1 w-100% p-r-2-L">
                        {as.requirePhoneAlertCredits && team.phoneAlertCredits <= 0 ?
                          <div className="d-f fb-d-c t-c-r m-b-1">
                            {t("Contains recipients that require Phone Alert Credits.")}&nbsp;
                            <Link to={`/${team.slug}/billing/phone-alert-credits`}>
                              {t("Click here to add credits")}.
                            </Link>
                          </div>
                        : null}
                        {as.showingRecipients ?
                          <>
                            {as.alert.recipients.map((r, j) =>
                              <div key={`${i}.${j}`} className="d-f a-i-c p-b-1">
                                <div className="fb-n w-2 d-f a-i-c">
                                  {r.type === "email" ?
                                    <IconEmail />
                                  : r.type === "slack" ?
                                    <IconSlack />
                                  : r.type === "discord" ?
                                    <IconDiscord />
                                : r.type === "sms" ?
                                    <IconSms />
                                  : r.type === "call" ?
                                    <IconCall />
                                  : r.type === "webhook" ?
                                    <IconWebhook />
                                  : null}
                                </div>
                                <div className="fb-a f-s-1 of-h t-o-e ws-n">
                                  {r.target}
                                </div>
                              </div>
                            )}
                            <div onClick={() => hideRecipients(as)} className="t-c-g c-p">
                              {t("Hide Recipients")}
                            </div>
                          </>
                        :
                          <div onClick={() => showRecipients(as)} className="t-c-g c-p">
                            {t("Show Recipients")}
                          </div>
                        }
                      </div>
                      <div className="d-n d-f-L fb-n w-14">
                        {as.alert.monitors.length > 0 ?
                          <div className="m-t-1">
                            <Link to={`/${team.slug}/monitors?alertUuid=${as.alert.uuid}`}>
                              {as.alert.monitors.length > 1 ?
                                t("View Monitors")
                              :
                                t("View Monitor")
                              }
                            </Link>
                          </div>
                        :
                          <div className="m-t-1">
                            <Link to={`/${team.slug}/monitors`}>
                              {t("Add  Monitors")}
                            </Link>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                  <div className="d-n fb-n d-f-L fb-d-c w-12">
                    {renderStatuses(as)}
                  </div>
                  {isOwnerOrMember ?
                    <div className="d-f fb-n j-c-e w-2" title={t("Options")}>
                      <MenuInRow items={[
                        {title: t("Edit"), onClick: () => setEditingSelector(as.alert) },
                        {title: t("Delete"), onClick: () => setRemovingSelector(as.alert)},
                      ]} />
                    </div>
                  : null}
                </div>
              )}
            </>
          }

        </div>

        <div className="d-f j-c-c">
          <Paginator
            recordOffset={paginator.recordOffset}
            recordsCount={paginator.recordsCount}
            pageSize={paginator.pageSize}
            url={`${history.location.pathname}${history.location.search}`} />
        </div>

        { editingSelector ?
          <AlertEdit
            alertUuid={editingSelector.uuid}
            onUpdate={onUpdate}
            onCancel={onCancelEdit} />
          : null
        }

        { removingAlert ?
          <Modal
            open={removingAlert !== undefined}
            onClose={ () => setRemovingSelector(undefined) }
            title={t("Delete Alert")}
            description={ removingAlert.name }
            secondaryActions={ [ { title: t("Cancel"), onAction: () => setRemovingSelector(undefined) } ] }
            primaryAction={ { title: t("Delete Alert"), onAction: execRemove, type: "destructive" } }>
            <div className="p-2">
              <div className="f-s-1.5 f-w-300 t-c-b">
                {t("You can't reverse this action!")}
              </div>
              <div className="f-s-1 f-w-300 m-t-0.5">
                {t("Removing Alerts won't remove the alerted monitors.")}
              </div>
            </div>
          </Modal>
          : null
        }

        { isSelectingForRemove ?
          <Modal
            open={isSelectingForRemove}
            onClose={ () => setIsSelectingForRemove(false) }
            title={t("Delete Alerts")}
            secondaryActions={ [ { title: t("Cancel"), onAction: () => setIsSelectingForRemove(false) } ] }
            primaryAction={ { title: t("Delete Alerts"), onAction: removeAlerts, type: "destructive" } }>

            <div className="p-2">
              <div className="f-s-1.5 f-w-300 t-c-b">
                {t("You can't reverse this action!")}
              </div>
              <div className="f-s-1 f-w-300 m-t-0.5">
                {t("Removing Alerts won't remove their monitors.")}
              </div>
            </div>
          </Modal>
          : null
        }
      </>
    );
  }
}
